/**
 * @author dhonehi
 * Данный composable подключается в каждом офере. При первом обращении к данному composable начинается загрузка списка сравнения.
 * Список сравнения передается через поле items, которое может принимать функцию, которая должна возвращать объект или массив.
 *
 * Объект должен быть вида {id_товара: значение'}
 * Массив должен быть вида [id_товара]
 *
 * При добавлении / удалении товара в список сравнения, вызывается функция onChangeCompareStatus,
 * которая в свою очередь вызывает callback, передаваемый в настройках сравнения. В данный callback передается payload,
 * который содержит информацию о товаре и поле isCompare, которое хранит информацию о том, офер в списке сравнения или нет.
 * Если callback ничего не возвращает, то новое значение будет равно противоположному.
 */

import { ref, watch } from '@vue/composition-api';

import ApiEventController from '@/init/ApiEventController';

const compareData = {
    compare: null,
    settings: null,
};

let isLoadedCompare = false;
let isLoadingCompare = false;

/** Загружает список сравнения*/
async function loadCompare() {
    isLoadingCompare = true;

    try {
        compareData.compare.value = null;

        if (typeof compareData.settings.items === 'function') {
            compareData.compare.value = await compareData.settings.items();
        } else {
            throw new Error('Compare items must be a function');
        }
    } catch (error) {
        console.error(error);
    } finally {
        isLoadingCompare = false;
        isLoadedCompare = true;
    }
}

/**
 * Проверяет в списке сравнения ли офер
 * @param {Object} offer - объект описания товара
 */
function checkIsCompareOffer(offer) {
    if (!compareData.compare.value) {
        return false;
    }

    if (Array.isArray(compareData.compare.value)) {
        return compareData.compare.value.includes(offer.id);
    }

    return !!compareData.compare.value[offer.id];
}

ApiEventController.on(ApiEventController.EVENTS.UPDATE_COMPARE, (event) => {
    if (!compareData.compare) {
        return;
    }

    const detail = event.detail;

    if (detail) {
        compareData.compare.value = {
            ...compareData.compare?.value,
            ...detail,
        };
    } else {
        loadCompare();
    }
});

/**
 * Composable функция, которая отвечает за работу со списком сравнения
 * Все что находится внутри useOfferCompareController, является уникальным состоянием для каждого компонента офера,
 * все что находится вне, является общим состоянием для всех компонентов офера
 * @param {Object} offer - объект описания товара
 * @param {Object} compareSettings - объект с настройками сравнения из конфига
 */
export default function useOfferCompareController(offer, compareSettings) {
    const isCompareOffer = ref(false);
    /** Проверяем наличие настроек сравнения*/
    const hasCompareSettings = !!compareSettings?.items;

    /** Загружаем список сравнения только один раз и только если он еще не загружен*/
    if (!isLoadedCompare && !isLoadingCompare) {
        /** Делаем список сравнения реактивным для того чтобы можно было повесить на него watch*/
        compareData.compare = ref(null);
        compareData.settings = compareSettings;

        if (hasCompareSettings) {
            loadCompare();
        }
    }

    /** Проверяем в списке сравнения ли офер */
    isCompareOffer.value = checkIsCompareOffer(offer);

    /**
     * Обработчик изменения статуса сравнения
     * @param {Boolean} isCompare
     */
    const onChangeCompareStatus = async (isCompare) => {
        try {
            const result = await compareData.settings.onChange({
                ...offer,
                isCompare,
            });

            isCompareOffer.value = typeof result === 'boolean' ? result : isCompare;

            window.sbEvents.pushEvent('UPDATE_COMPARE', { [offer.id]: isCompareOffer.value });
        } catch (error) {
            console.error(error);
        }
    };

    watch(compareData.compare, () => {
        if (compareData.compare.value) {
            isCompareOffer.value = checkIsCompareOffer(offer);
        }
    });

    return {
        isCompareOffer,
        onChange: hasCompareSettings && compareData.settings.onChange ?
            onChangeCompareStatus :
            () => {
            },
    };
}
